/**
 * Calendar dates as `YYYY-MM-DD` strings — the one implementation of the
 * trial's day arithmetic, shared by the api and the admin panel
 * (registration-approval arch §5.3, N-12).
 *
 * "Today" is the UTC calendar date everywhere: the playback gate has always
 * compared against the database's UTC date, and the admin reads today from the
 * server (`GET /admin/server-date`) instead of computing it. From 00:00 to 03:00
 * MSK the UTC today is the MSK yesterday — accepted by the PRD (R-7).
 *
 * All arithmetic runs on UTC midnights, so the host's time zone never shifts a
 * date. A string that is not a real calendar date throws `RangeError` rather
 * than rolling over (`Date.UTC` would turn 30 February into 2 March).
 */
const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DAY_MS = 86_400_000;
function toIsoDate(ms) {
    return new Date(ms).toISOString().slice(0, 10);
}
function parseIsoDate(value) {
    const match = ISO_DATE_RE.exec(value);
    if (!match) {
        throw new RangeError(`Expected a YYYY-MM-DD date, got "${value}"`);
    }
    const ms = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    if (toIsoDate(ms) !== value) {
        throw new RangeError(`"${value}" is not a calendar date`);
    }
    return ms;
}
/**
 * Whether `value` is a real `YYYY-MM-DD` calendar date: 2026-02-29 and
 * 2026-11-31 are not, and Postgres refuses to store them as a `date`.
 */
export function isCalendarDateIso(value) {
    const match = ISO_DATE_RE.exec(value);
    if (!match)
        return false;
    const ms = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return toIsoDate(ms) === value;
}
/** The UTC calendar date of `now`. */
export function todayUtcIso(now = new Date()) {
    return toIsoDate(now.getTime());
}
/** `date` plus a whole number of days (negative counts go back). */
export function addDaysIso(date, days) {
    if (!Number.isInteger(days)) {
        throw new RangeError(`Expected a whole number of days, got ${days}`);
    }
    return toIsoDate(parseIsoDate(date) + days * DAY_MS);
}
/**
 * The last day a period with the exclusive end `end` covers: a trial with
 * `period_end` 2026-10-15 plays through 2026-10-14.
 */
export function lastDayOf(end) {
    return addDaysIso(end, -1);
}
//# sourceMappingURL=calendar-date.js.map